import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Spinner from "../../components/helpers/Spinner";
import ResponsiveModalAbsolute from "../../components/ResponsiveModalAbsolute";
import { fetchAxiosToken } from "../../helpers/fetchAxiosToken";
import { useAppSelector } from "../../hooks/hooks";
import { useDispatchDominio } from "../../hooks/useDispatchDominio";

interface Props {
    modalVisible: boolean;
    setModalVisible: (s: boolean) => void;
    id_habitacion: number;
}

interface TipoHabitacion {
    id: number;
    id_casa: number;
    nombre: string;
}

export const ModalEditTipoHabitacion = ({
    modalVisible,
    setModalVisible,
    id_habitacion,
}: Props) => {
    const { id: casa_id } = useAppSelector((s) => s.landing_house.house);
    const dispatchDominio = useDispatchDominio();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tipos, setTipos] = useState<TipoHabitacion[]>([]);
    const [selectedTipoId, setSelectedTipoId] = useState<number | "">("");
    const [loadingTipos, setLoadingTipos] = useState(false);

    const selectedTipo = useMemo(
        () => tipos.find((t) => t.id === selectedTipoId),
        [tipos, selectedTipoId]
    );

    const getTiposHabitacion = async () => {
        if (!casa_id) return;
        try {
            const res = await fetchAxiosToken({
                url: `/tipo_hotel_habitaciones/getAll/${casa_id}`,
                method: "get",
            });
            console.log("getTiposHabitacion", getTiposHabitacion);
            const data = (res?.data?.data ??
                res?.data ??
                res?.payload ??
                res) as TipoHabitacion[] | undefined;
            setTipos(Array.isArray(data) ? data : []);
        } catch (e) {
            console.log(e);
            setTipos([]);
        }
    };

    const getDetalleHabitacion = async () => {
        try {
            const res = await fetchAxiosToken({
                url: `/hotel_habitaciones/detalle/${id_habitacion}`,
            });
            const data = (res?.data?.data ?? res?.data ?? res) as any;
            console.log("getDetalleHabitacion", getDetalleHabitacion);
            const idTipo = Number(
                data?.id_tipo_hotel_habitacion ??
                    data?.id_tipo_hotel_habitaciones
            );
            if (!Number.isNaN(idTipo)) setSelectedTipoId(idTipo);
        } catch (e) {
            console.log(e);
        }
    };

    const boot = async () => {
        setLoading(true);
        await Promise.all([getTiposHabitacion(), getDetalleHabitacion()]);
        setLoading(false);
    };

    useEffect(() => {
        if (modalVisible) boot();
    }, [modalVisible]);

    const crearTipo = async () => {
        const { value: nombre } = await Swal.fire({
            title: "Nuevo tipo de habitación",
            input: "text",
            inputLabel: "Nombre del tipo",
            inputPlaceholder: "ej. Suite Deluxe",
            confirmButtonText: "Crear",
            showCancelButton: true,
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#029BFC",
            inputValidator: (value) => {
                if (!value?.trim()) return "Debes ingresar un nombre válido";
                return null;
            },
        });
        if (!nombre) return;
        setLoadingTipos(true);
        try {
            const res = await fetchAxiosToken({
                url: `/tipo_hotel_habitaciones`,
                method: "post",
                body: { nombre: nombre.trim() },
            });

            const success = res?.data?.success ?? res?.success ?? true;
            const message = res?.data?.message ?? res?.message ?? "";
            const creado = res?.data?.data as TipoHabitacion | undefined;

            if (
                success === false &&
                typeof message === "string" &&
                message.toLowerCase().includes("ya existe")
            ) {
                await getTiposHabitacion();
                const existente = tipos.find(
                    (t) =>
                        t.nombre.toLowerCase().trim() ===
                        nombre.toLowerCase().trim()
                );
                if (existente) setSelectedTipoId(existente.id);
                await Swal.fire({
                    icon: "warning",
                    title: "El tipo ya existe",
                    text: "Seleccioné el existente.",
                    confirmButtonColor: "#029BFC",
                });
                return;
            }

            if (creado?.id) {
                const nuevos = [...tipos, creado].sort((a, b) =>
                    a.nombre.localeCompare(b.nombre)
                );
                setTipos(nuevos);
                setSelectedTipoId(creado.id);
                Swal.fire({
                    icon: "success",
                    title: "Tipo creado",
                    timer: 1200,
                    showConfirmButton: false,
                });
            } else {
                await getTiposHabitacion();
                const recien = tipos.find(
                    (t) =>
                        t.nombre.toLowerCase().trim() ===
                        nombre.toLowerCase().trim()
                );
                if (recien) setSelectedTipoId(recien.id);
                Swal.fire({
                    icon: "success",
                    title: "Tipo creado",
                    timer: 1200,
                    showConfirmButton: false,
                });
            }
            setLoadingTipos(false);
        } catch (error: any) {
            console.log(error);
            Swal.fire({
                icon: "error",
                title: "No se pudo crear el tipo",
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Intenta nuevamente",
            });
            setLoadingTipos(false);
        }
    };

    const editarTipo = async (tipo: TipoHabitacion) => {
        const { value: nombre } = await Swal.fire({
            title: "Editar tipo de habitación",
            input: "text",
            inputLabel: "Nuevo nombre",
            inputValue: tipo.nombre,
            inputPlaceholder: "ej. Suite Deluxe",
            confirmButtonText: "Guardar",
            showCancelButton: true,
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#029BFC",
            inputValidator: (value) => {
                if (!value?.trim()) return "Debes ingresar un nombre válido";
                return null;
            },
        });
        if (!nombre || nombre.trim() === tipo.nombre.trim()) return;

        try {
            const res = await fetchAxiosToken({
                url: `/tipo_hotel_habitaciones/updateTipo/${tipo.id}`,
                method: "put",
                body: { nombre: nombre.trim() },
            });

            const success = res?.data?.success ?? res?.success ?? true;
            const message = res?.data?.message ?? res?.message ?? "";
            const actualizado = res?.data?.data as TipoHabitacion | undefined;

            if (
                success === false &&
                typeof message === "string" &&
                message.toLowerCase().includes("ya existe")
            ) {
                await Swal.fire({
                    icon: "warning",
                    title: "El nombre ya existe",
                    text: "Intenta con otro nombre.",
                    confirmButtonColor: "#029BFC",
                });
                return;
            }

            const nuevoNombre = actualizado?.nombre ?? nombre.trim();
            setTipos((prev) =>
                prev
                    .map((t) =>
                        t.id === tipo.id ? { ...t, nombre: nuevoNombre } : t
                    )
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
            );

            Swal.fire({
                icon: "success",
                title: "Tipo actualizado",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error: any) {
            console.log(error);
            Swal.fire({
                icon: "error",
                title: "No se pudo actualizar",
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Intenta nuevamente",
            });
        }
    };

    const eliminarTipo = async (tipo: TipoHabitacion) => {
        const ok = await Swal.fire({
            icon: "warning",
            title: "Eliminar tipo",
            text: `¿Seguro que deseas eliminar "${tipo.nombre}"? Esta acción no se puede deshacer.`,
            showCancelButton: true,
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#e11d48",
        });
        if (!ok.isConfirmed) return;

        try {
            const res = await fetchAxiosToken({
                url: `/tipo_hotel_habitaciones/deleteTipo/${tipo.id}`,
                method: "delete",
            });

            const success = res?.data?.success ?? res?.success ?? true;
            const message = res?.data?.message ?? res?.message ?? "";

            if (success === false) {
                await Swal.fire({
                    icon: "error",
                    title: "No se pudo eliminar",
                    text: message || "Intenta nuevamente",
                    confirmButtonColor: "#029BFC",
                });
                return;
            }

            setTipos((prev) => prev.filter((t) => t.id !== tipo.id));
            if (selectedTipoId === tipo.id) setSelectedTipoId("");

            Swal.fire({
                icon: "success",
                title: "Tipo eliminado",
                timer: 1000,
                showConfirmButton: false,
            });
        } catch (error: any) {
            console.log(error);
            Swal.fire({
                icon: "error",
                title: "Error al eliminar",
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Intenta nuevamente",
            });
        }
    };

    const handleSubmit = async () => {
        if (!selectedTipoId) {
            return Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Debes seleccionar un tipo",
                icon: "warning",
                confirmButtonText: "Aceptar",
            });
        }

        try {
            setSaving(true);

            await fetchAxiosToken({
                url: `/hotel_habitaciones/updateNombre/${id_habitacion}`,
                method: "put",
                body: { id_tipo_hotel_habitaciones: Number(selectedTipoId) },
            });

            dispatchDominio();

            await Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Actualizado",
                text: "Tipo de habitación actualizado",
                icon: "success",
                confirmButtonText: "Ok",
            });

            setSaving(false);
            setModalVisible(false);
        } catch (error) {
            console.log(error);
            setSaving(false);
            Swal.fire({
                icon: "error",
                title: "Error al guardar",
                text: "Intenta nuevamente",
                confirmButtonColor: "#029BFC",
            });
        }
    };

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}>
            <div className="flex flex-col items-center justify-center max-w-2xl gap-4 p-4 mx-auto text-gray-700">
                <h2 className="2xl:text-4xl text-3xl text-[#1C4CBB] text-center font-medium">
                    Tipo de habitación
                </h2>
                <hr className="w-full bg-gray-300" />

                {loading ? (
                    <div className="py-10">
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col w-11/12">
                            <label className="ml-2 text-lg font-medium 2xl:text-xl">
                                Selecciona el tipo
                            </label>
                            <div className="flex items-center gap-2 mt-2">
                                <select
                                    className="w-full p-3 text-black border rounded-lg border-[#1C4CBB]"
                                    value={selectedTipoId}
                                    onChange={(e) =>
                                        setSelectedTipoId(
                                            e.target.value
                                                ? Number(e.target.value)
                                                : ""
                                        )
                                    }>
                                    <option value="">Selecciona un tipo</option>
                                    {tipos
                                        .slice()
                                        .sort((a, b) =>
                                            a.nombre.localeCompare(b.nombre)
                                        )
                                        .map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.nombre}
                                            </option>
                                        ))}
                                </select>

                                <button
                                    type="button"
                                    onClick={crearTipo}
                                    className="flex items-center h-full gap-2 px-3 py-3.5 text-sm font-semibold border border-gray-500 rounded-lg hover:bg-gray-50"
                                    disabled={loadingTipos}
                                    title="Crear nuevo tipo">
                                    <p>+</p> Nuevo
                                </button>
                            </div>
                            <p className="mt-1 text-sm text-left text-gray-500">
                                Campo obligatorio.
                            </p>
                        </div>

                        <div className="w-11/12 mt-4">
                            <h3 className="mb-2 text-base font-semibold">
                                Gestionar tipos existentes
                            </h3>
                            <div className="overflow-y-auto border rounded-lg max-h-64">
                                {tipos.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-500">
                                        No hay tipos creados.
                                    </div>
                                ) : (
                                    <ul className="divide-y">
                                        {tipos
                                            .slice()
                                            .sort((a, b) =>
                                                a.nombre.localeCompare(b.nombre)
                                            )
                                            .map((t) => (
                                                <li
                                                    key={t.id}
                                                    className={`flex items-center justify-between px-3 py-2 text-sm ${
                                                        selectedTipoId === t.id
                                                            ? "bg-blue-50"
                                                            : ""
                                                    }`}>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name="tipo"
                                                            checked={
                                                                selectedTipoId ===
                                                                t.id
                                                            }
                                                            onChange={() =>
                                                                setSelectedTipoId(
                                                                    t.id
                                                                )
                                                            }
                                                        />
                                                        <span className="font-medium">
                                                            {t.nombre}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            className="px-2 py-1 border rounded hover:bg-gray-50"
                                                            onClick={() =>
                                                                editarTipo(t)
                                                            }
                                                            title="Editar nombre">
                                                            Editar
                                                        </button>
                                                        <button
                                                            className="px-2 py-1 border rounded text-rose-600 hover:bg-rose-50"
                                                            onClick={() =>
                                                                eliminarTipo(t)
                                                            }
                                                            title="Eliminar tipo">
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="w-11/12 mt-4 2xl:mt-6">
                            {saving ? (
                                <Spinner />
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="w-full p-3 px-5 text-white rounded-lg bg-[#1C4CBB] hover:scale-105 2xl:text-base text-sm disabled:opacity-60"
                                    disabled={!selectedTipoId}>
                                    Guardar cambios
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </ResponsiveModalAbsolute>
    );
};
